import React, { FC, useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import {
  Calendar as BigCalendar,
  luxonLocalizer,
  View,
} from "react-big-calendar";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

const localizer = luxonLocalizer(DateTime);

const getMessages = (t: TFunction) => {
  return {
    date: t("calendar.date"),
    time: t("calendar.time"),
    event: t("calendar.event"),
    allDay: t("calendar.allDay"),
    week: t("calendar.week"),
    work_week: t("calendar.workWeek"),
    day: t("calendar.day"),
    month: t("calendar.month"),
    previous: t("calendar.previous"),
    next: t("calendar.next"),
    yesterday: t("calendar.yesterday"),
    tomorrow: t("calendar.tomorrow"),
    today: t("calendar.today"),
    agenda: t("calendar.agenda"),
    noEventsInRange: t("calendar.noEventsInRange"),
    showMore: (
      total: number,
      remainingEvents: CalendarEvent[],
      events: CalendarEvent[],
    ) => t(`showMore`, { total, remainingEvents, events }),
  };
};

export interface CalendarEvent {
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource?: any;
}

const Calendar: FC<{
  events: CalendarEvent[];
  date: Date;
  view: View;
  onViewChange: (view: View) => void;
  onNavigate: (newDate: Date) => void;
}> = ({ events, date, view, onViewChange, onNavigate }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const messages = useMemo(() => {
    return getMessages(t);
  }, [t]);

  const onSelectEvent = useCallback(
    (e: CalendarEvent) => {
      onViewChange("day");
      onNavigate(e.start);
    },
    [onViewChange, onNavigate],
  );

  return (
    <BigCalendar
      date={date}
      localizer={localizer}
      events={events}
      culture={language}
      messages={messages}
      onView={onViewChange}
      view={view}
      onNavigate={onNavigate}
      onSelectEvent={onSelectEvent}
    />
  );
};

export default Calendar;
